// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './AlertStats.scss';
// Service imports:
import { _for, isReqError } from '../../services/utils';
// Action imports:
import { setSelectedStatus } from '../../redux-store/actions/alertActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { AlertStatus, AlertTickData, IAlert } from '../../types/api/AlertTypes';
import { ByTradingDay, Requested } from '../../types/api/Types';

type ReduxProps = ConnectedProps<typeof connector>;
type OwnProps = {
    isLinks?: boolean,
    dayRange: {
        fromDate: string,
        toDate: string
    } | undefined,
    currentTradingDay: string | undefined
    alertsByDate: ByTradingDay<Requested<AlertTickData[]>>
}
type Props = ReduxProps & OwnProps;

const setJsxBadgesControler = (
    badgesInfo: any,
    alertsForBadges: IAlert[],
    selectedStatus: AlertStatus | null,
    currentTradingDay: string | undefined,
    isLinks: boolean | undefined,
    isLoadingOrIsError: boolean,
    setSelectedStatus: (status: AlertStatus | null) => void
) =>{
    const jsxBadges = badgesInfo.map(({ title, type }: { title: string, type: AlertStatus }) => {
        const count = (type === 'forreview')
            ? alertsForBadges.filter(alert => alert.status === null).length
            : alertsForBadges.filter(alert => alert.status === type).length
        // Calculate classes.
        const isSelectedClassName = (selectedStatus === type) ? 'SelectedStatus' : '';
        // Calculate href based on props.
        const href = (isLinks) ? `/alertsall?status=${type}&fromDate=${currentTradingDay}&toDate=${currentTradingDay}` : undefined;
        return (
            <a
                key={type}
                className={`AlertStatusWrapper ${isSelectedClassName}`}
                href={href}
                onClick={() => {
                    if (!isLinks) {
                        if (selectedStatus === type) {
                            setSelectedStatus(null);
                        } else {
                            setSelectedStatus(type);
                        }
                    }
                }}
            >
                <div className={`Circle StatusColorWithText ${type}`}>
                    <span>
                        {(isLoadingOrIsError) ? '-' : count}
                    </span>
                </div>
                <span className='StatusText'>{title}</span>
            </a>
        );
    });
    return jsxBadges;
}

/**
 *  This component displays the alert stats. **For NavBar Alerts and Overview**
 *  - Subcomponent of AlertsAll.tsx
 *  - Subcomponent of SigtiHome.tsx
 **/
const AlertStats: React.FC<Props> = ({
    selectedStatus,
    setSelectedStatus,
    isLinks,
    dayRange,
    currentTradingDay,
    alertsByDate
}) => {
    const displayAlertBadges = () => {
        const alertsForBadges: Array<IAlert> = [];
        let isLoadingOrIsError = false;
        let errorMessage: string | null = null;

        // Single Day.
        if (currentTradingDay !== undefined) {
            const alerts = alertsByDate[currentTradingDay];
            if (alerts === undefined || alerts === 'waiting') {
                isLoadingOrIsError = true;
            } else if (isReqError(alerts)){
                isLoadingOrIsError = true;
                errorMessage = alerts.message;
            } else {
                alertsForBadges.push(...alerts);
            }
        }
        // Day range.
        else if (dayRange !== undefined) {
            const { fromDate, toDate } = dayRange;
            _for(fromDate, toDate, (currentDate) => {
                const alerts = alertsByDate[currentDate];
                if (alerts === undefined || alerts === 'waiting') {
                    isLoadingOrIsError = true;
                    return 'break';
                } else if (isReqError(alerts)) {
                    isLoadingOrIsError = true;
                    errorMessage = alerts.message;
                    return 'break';
                } else {
                    alertsForBadges.push(...alerts);
                }
            });
        }

        interface IBadgeInfo {
            title: string;
            type: AlertStatus;
        }

        const badgesInfo: IBadgeInfo[] = [{
            title: 'For review',
            type: 'forreview'
        }, {
            title: 'Open',
            type: 'open'
        }, {
            title: 'Watch',
            type: 'watch'
        }, {
            title: 'Investigating',
            type: 'investig'
        }, {
            title: 'Critical',
            type: 'critical'
        }, {
            title: 'Explained',
            type: 'explained'
        }, {
            title: 'Closed',
            type: 'closed'
        }];

        // If the badges are links, we display a tags with hrefs.
        // If they are not links, they should be used to change the selected statuses redux attribute.
        const jsxBadges = setJsxBadgesControler(
            badgesInfo, alertsForBadges, 
            selectedStatus, currentTradingDay, 
            isLinks, isLoadingOrIsError, 
            setSelectedStatus
        );

        if (errorMessage) {
            return <div className='error-message'>{errorMessage}</div>
        }
        
        return jsxBadges;
    }

    return (
        <div id='AlertStats'>
            <div className='StatusButtonWrapper'>
                {displayAlertBadges()}
            </div>
        </div>
    );
}

function mapStateToProps({ alertReducer }: RootState) {
    const { selectedStatus } = alertReducer;
    return {
        selectedStatus
    }
}

const connector = connect(mapStateToProps, { setSelectedStatus });
export default connector(AlertStats);
